/**
 * 機台管理（ECOPD-5655）的資料型別。
 *
 * 這一組型別直接對應後端 `/v2/device-management/*` 的 wire 格式，欄位名刻意
 * 與回應一致，避免在轉換層再改一次名字。
 *
 * 名字對不上後端時，型別系統看不出來：這裡宣告什麼、消費點就讀什麼，前端內部自洽，
 * 而 tsc 看不到後端。唯一擋得住的是 ws 層的測試，見 ws/DeviceManagementWebService.test.ts。
 */

/**
 * 清單的單位層級。門市層一列代表一間門市，機台層一列代表一台機器。
 * 由後端決定：渠道的投遞單位是門市還是機台，清單就跟著是哪一層。
 */
export enum DeviceUnitLevel {
  STORE = 'STORE',
  DEVICE = 'DEVICE'
}

/**
 * 銷售渠道。走 `/v2/device-management/channels` 而不是共用的渠道清單：
 * 哪些渠道在這一頁看得到（例如電子紙要不要排除）由後端的單一入口判定，
 * 前端比對渠道型別字串複製不出同一套規則。
 */
export type DeviceChannel = {
  channelId: number;
  channelName: string;
  deliveryUnit: DeviceUnitLevel;
};

export type DeviceUnitRecord = {
  unitKey: string;
  unitLevel: DeviceUnitLevel;
  unitName: string;
  storeNum: string;
  /** 門市層沒有機台特徵，後端回 null。 */
  deviceCharacteristic: string | null;
  /** 後端的大寫格式；門市層已由後端聚合成單一狀態。 */
  healthState: string;
  healthStateReason?: string;
  lastReportedAt?: string;
  region?: string;
  district?: string;
  address?: string;
  /**
   * 一週七天的營業時間。不存在代表這個點位沒有營業時間資料；有資料時一定是七筆，
   * 某一天的 `openHours` 是空陣列代表那天不營業 —— 兩者在畫面上是不同的話。
   */
  businessHours?: DeviceBusinessHoursDay[];
};

/**
 * 某一天的營業時間。`dayOfWeek` 1 是週一、7 是週日；`openHours` 是當天有營業的小時
 * （0 到 23，遞增），空陣列是那天不營業。
 */
export type DeviceBusinessHoursDay = {
  dayOfWeek: number;
  openHours: number[];
};

export type DeviceUnitPagination = {
  /**
   * 1 起算。查詢送出的 `page` 是 0 起算，回應回來的是 1 起算，
   * 這個不對稱是既有契約，兩邊都照著寫。
   */
  page: number;
  size: number;
  totalCount: number;
};

export type DeviceUnitList = {
  records: DeviceUnitRecord[];
  pagination: DeviceUnitPagination;
  /**
   * 資料同步時間，顯示在頁首右上。清單為空時後端會整個省略這個欄位，
   * 所以呼叫端要能承受它不存在。
   */
  lastSyncedAt?: string;
};

/**
 * 人流讀數。`null` 是「沒有讀數」，與「讀數是 0」意思相反，
 * 所以型別上不給預設值，顯示層也不可以折成 0。
 *
 * 欄位名 `footfall` 是後端送出的名字，不是前端自己取的 —— 這一組型別照抄 wire 格式。
 */
export type DeviceFootTrafficPoint = {
  date: string;
  footfall: number | null;
};

/**
 * 人流資料的出處。`UNAVAILABLE` 是「問不到」，不是「量到 0」——
 * 目前資料源不可信，所有人流欄位都是 null + UNAVAILABLE。
 */
export enum DeviceFootTrafficSource {
  RESOLVED = 'RESOLVED',
  UNAVAILABLE = 'UNAVAILABLE'
}

export type DeviceUnitDetail = {
  unitKey: string;
  unitName: string;
  healthState: string;
  healthStateReason?: string;
  lastReportedAt?: string;
  region?: string;
  district?: string;
  /** 階層標籤，後端已組成 `parent > child`，前端原樣顯示。 */
  placeCategoryLabels: string[];
  latitude?: number;
  longitude?: number;
  /** 解析度是渠道層的設定，同渠道所有機台都是同一組值，這不是錯。 */
  screenPixelWidth?: number;
  screenPixelHeight?: number;
  /**
   * 日均人流（不含今天的前七個完整日）。查詢區間內一列投遞紀錄都沒有時是 null，
   * 不是 0 —— 「還沒問到」與「沒有人經過」對使用者的意思相反。
   *
   * 沒有活動投放的時段本來就不會有列，所以沒賣出去的機台這一欄多半是空的。
   */
  dailyAverageFootfall: number | null;
  last7DaysFootfall: DeviceFootTrafficPoint[];
  /**
   * 人流數字的出處。目前畫面不讀它：後端讓它與 `dailyAverageFootfall` 同進退
   * （`UNAVAILABLE` 時數值必為 null），所以「不知道」在畫面上已經由 null 表達完整。
   * 留著是因為它是端點回傳的欄位，而 `DeviceManagementWebService` 的映射測試釘著它 ——
   * 哪天兩者不再同進退，要改的是這裡而不是再從 null 猜一次。
   */
  footfallProvenance: DeviceFootTrafficSource;
};

export type DeviceUnitSortOrder = 'asc' | 'desc';

/** 清單查詢條件。`page` 是 0 起算。 */
export type DeviceUnitQuery = {
  channelId: number;
  page: number;
  size: number;
  sortField: string;
  sortOrder: DeviceUnitSortOrder;
  /** 搜尋門市名稱 / 門市店號 / 地址。 */
  search: string;
  /** wire 參數名 → 值，見 containers/DeviceManagement/deviceFilters.ts。 */
  filter: Record<string, string[]>;
  /**
   * 匯入名單限定的店號。空陣列代表沒有名單條件，不是「限定在空名單裡」。
   *
   * 與 `filter` 是疊加（AND）的，不是互斥：名單框住母體，其他條件在名單裡面再篩。
   */
  storeNums: string[];
};

/**
 * 匯出的範圍：不給 limit 表示全部。
 *
 * 走期與投放時段只有可售率分頁會帶。排序鍵落在那一頁才有的欄位（可售率、可售秒數、
 * 已售秒數、日均人流）時，後端要靠這兩個條件才算得出值，沒帶就是 400。清單檢視排得動的
 * 欄位不需要它們，所以是選填而不是拆成兩個型別 —— 兩個分頁送的是同一支端點。
 */
export type DeviceUnitExportQuery = Omit<DeviceUnitQuery, 'page' | 'size'> & {
  limit?: number;
} & Partial<
    Pick<DeviceAvailabilityQuery, 'startDate' | 'endDate' | 'dayparts'>
  >;

/**
 * 營業時段的出處，決定可售率那一組欄位怎麼讀。
 *
 * `MISSING` 與 `CLOSED_ALL_WEEK` 在畫面上必須是兩種顯示，不可以共用同一條分支：
 * 前者是「這個點位沒有營業時段資料，所以算不出來」，四個數值欄位全是 null；
 * 後者是「算得出來，答案就是整週都不營業」，`totalSellableSeconds` 是實實在在的 0。
 * 把兩者畫成同一句話，等於告訴業務「這裡一秒都賣不掉」，但其中一半其實只是我們不知道。
 */
export enum DeviceBusinessHoursProvenance {
  RESOLVED = 'RESOLVED',
  MISSING = 'MISSING',
  CLOSED_ALL_WEEK = 'CLOSED_ALL_WEEK'
}

/**
 * 機台可售狀況的一列：清單那一組欄位再加上走期內的可售統計。
 *
 * `MISSING` 時下面四個數值欄位一律是 null，不是 0 —— 已售秒數也在內：空間佔用型的
 * PMP 吃掉的是「那個小時的營業秒數」，而營業秒數正是不知道的那一個量，所以回一個
 * 看起來像樣的已售秒數，只會讓畫面顯示「賣掉了 N 秒、但不知道總共有多少」。
 *
 * 四個值都直接用後端給的，不要拿其中兩個去推第三個：超賣時已售秒數不夾住
 * （超賣要看得見），可售秒數與可售率則夾在 0，那時三者本來就湊不出減法。
 */
export type DeviceAvailabilityRecord = DeviceUnitRecord & {
  /** 日均人流；null 是「沒有讀數」不是「量到 0」，口徑與詳情側面板同一支。 */
  dailyAverageFootfall: number | null;
  /** 分母：走期內落在投放時段的營業秒數。 */
  totalSellableSeconds: number | null;
  /** 超賣時不夾住，可能大於 totalSellableSeconds。 */
  soldSeconds: number | null;
  /** 超賣時夾在 0，所以不等於 totalSellableSeconds 減 soldSeconds。 */
  availableSeconds: number | null;
  /** 0 到 1 的分數，不是百分比；要顯示成百分比得自己乘 100。超賣時夾在 0。 */
  availabilityRate: number | null;
  businessHoursProvenance: DeviceBusinessHoursProvenance;
  /**
   * 走期內把這個點位包走的 PMP 型態（`BUYOUT` 包台 / `SPONSORSHIP` 包版），沒有就是空陣列。
   * **包量不算** —— 它不把版位整段包走。
   *
   * 與秒數是兩個問題：秒數回答「還剩多少可以賣」，這一個回答「有沒有被包走」，所以它不受
   * 投放時段影響，也不會因為那檔活動缺出價而變成空的。
   */
  pmpTypes: string[];
};

/**
 * 走期內的彙總。
 *
 * 每個數值欄位都可能是 null，意思一律是「這個數字問不到」——後端沒給、或給了
 * 一個算不出來的值。折成 0 會讓畫面說出一句假話（「可售 0 秒」），所以在 ws 層
 * 就折成 null，由顯示層畫成「沒有值」。型別上留著 null，消費端才不會忘記這一種。
 */
export type DeviceAvailabilitySummary = {
  deliverableUnitCount: number | null;
  totalUnitCount: number | null;
  /** 同樣是 0 到 1 的分數。整份查詢都算不出來時是 null。 */
  overallAvailabilityRate: number | null;
  totalSellableSeconds: number | null;
  totalSoldSeconds: number | null;
  totalAvailableSeconds: number | null;
  /** 沒有營業時段資料、因此沒被算進上面幾個總計的點位數。 */
  missingBusinessHoursUnitCount: number | null;
  /** 已售秒數算不進來的活動數；大於 0 代表已售秒數是低估。 */
  uncomputableCampaignCount: number | null;
};

export type DeviceAvailabilityList = {
  records: DeviceAvailabilityRecord[];
  pagination: DeviceUnitPagination;
  summary: DeviceAvailabilitySummary;
  lastSyncedAt?: string;
};

/**
 * 送給後端的投放時段：key 是星期（1 是週一、7 是週日），值是該天的小時 0 到 23。
 *
 * 後端對非數字的 key 顯式拒絕並回 400，所以組這份物件的時候一定要把畫面用的
 * `enabled` 旗標拿掉，見 containers/DeviceManagement/availabilityDayparts.ts。
 */
export type DeviceDaypartsParam = Record<string, number[]>;

/**
 * 可售率查詢：清單那一組條件再加走期與投放時段。日期是 yyyy-MM-dd。
 *
 * 走期最長 366 天，超過後端回 400。
 */
export type DeviceAvailabilityQuery = DeviceUnitQuery & {
  startDate: string;
  endDate: string;
  dayparts: DeviceDaypartsParam;
};

export type DeviceAvailabilityCalendarPoint = {
  date: string;
  /** 0 到 1 的分數；那一天沒有落在投放時段時是 null。 */
  availabilityRate: number | null;
  businessHoursProvenance: DeviceBusinessHoursProvenance;
};

/** 詳情裡「這個點位被哪些 PMP 包走」的一列。 */
export type DevicePmpBooking = {
  /** 訂單名稱；查不到訂單時是 null。 */
  orderName: string | null;
  /** 走期取 PMP 自己的起迄，不是活動的。 */
  startTime: string | null;
  endTime: string | null;
  /** 經銷商名稱；查不到訂單時是 null。 */
  agencyName: string | null;
};

export type DevicePmpBookingQuery = {
  channelId: number;
  unitKey: string;
  startDate: string;
  endDate: string;
};

export type DeviceAvailabilityCalendarQuery = {
  channelId: number;
  unitKey: string;
  startDate: string;
  endDate: string;
  dayparts: DeviceDaypartsParam;
};
